import React, { useMemo, useRef, useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import IconButton from "../button/IconButton";
import CommentField from "../comment-field/CommentField";
import UploadProgressBar from "../upload-progress/UploadProgressBar";
import SoremonCharacter from "./SoremonCharacter";
import SoremonMessageList from "./SoremonMessageList";
import { soremonMoodText } from "./soremonMoodText";
import { useSoremonPanelOpen } from "./soremonStore";
import { useSoremonActivity } from "./useSoremonActivity";
import { ACCEPT_FILE_TYPES, useSoremonAttachments } from "./useSoremonAttachments";
import { useSoremonAvailable } from "./useSoremonAvailable";
import { useSoremonPanelVisibility } from "./useSoremonPanelVisibility";
import {
  GLOBAL_HEADER_TRANSITION,
  OVERLAY_BOTTOM_MARGIN,
  useGlobalHeaderOffset,
} from "../global/headerOverlay";
import {
  useCancelSoremonActionMutation,
  useConfirmSoremonActionMutation,
  useGetSoremonConversationQuery,
  useResetSoremonConversationMutation,
  useSendSoremonMessageMutation,
} from "../../app/services/soremon";
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import { extractErrorMessage } from "../../common/utils";
import type { MessageUser } from "../../types/message";

const MAX_INPUT_CHARS = 2000;
const ERROR_FALLBACK = "ここで止まりました。少し時間をおいて、もう一度話しかけてもらえますか？";

/* 開閉アニメーションはユーザーアイコンのポップオーバー（account-popover-tippy.css の
 * scale）と同じ: 上から下へ伸びる。開く 150ms / 閉じる 100ms。 */
const OPEN_EASING = "cubic-bezier(0.25, 0.46, 0.45, 0.94)";
const OPEN_MS = 150;
const CLOSE_MS = 100;

/* ヘッダーの真下（プロジェクトバナーの上端と同じ高さ）から、ヘッダーが隠れていれば
 * 画面の一番上から出し、画面の下端まで使う。右端はヘッダーの soremon アイコンに揃える。 */
const Panel = styled.section<{ $top: number; $open: boolean }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  right: 16px;
  z-index: 100;
  height: calc(100vh - ${({ $top }) => $top + OVERLAY_BOTTOM_MARGIN}px);
  border-radius: 12px;
  border: 1px solid var(--soremo-border);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  transform-origin: top right;
  transform: scaleY(${({ $open }) => ($open ? 1 : 0)});
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
  pointer-events: ${({ $open }) => ($open ? "auto" : "none")};
  transition:
    transform ${({ $open }) => ($open ? OPEN_MS : CLOSE_MS)}ms ${OPEN_EASING},
    opacity ${({ $open }) => ($open ? OPEN_MS : CLOSE_MS)}ms ${OPEN_EASING},
    visibility 0s linear ${({ $open }) => ($open ? 0 : CLOSE_MS)}ms,
    top ${GLOBAL_HEADER_TRANSITION},
    height ${GLOBAL_HEADER_TRANSITION};
`;

/**
 * AI アシスタント soremon のチャットパネル（App 直下に 1 つだけ置く）。
 *
 * ヘッダー右端の SoremonHeaderButton のホバーで開き、外側クリックか Esc で閉じる
 * （useSoremonPanelVisibility）。メッセージは MessageItem、入力欄は CommentField を使い、
 * トークルーム / DM と見た目・操作をそろえる。確認が必要な操作はメッセージ下の
 * 確認カードで実行 / キャンセルする。
 */
const SoremonPanel: React.FC = () => {
  const available = useSoremonAvailable();
  const open = useSoremonPanelOpen();
  const panelTop = useGlobalHeaderOffset();
  const panelRef = useRef<HTMLElement>(null);
  const { mounted, shown } = useSoremonPanelVisibility(open, panelRef);
  const [sendingText, setSendingText] = useState<string | null>(null);

  const { data: currentUser } = useGetCurrentUserQuery(undefined, { skip: !available });
  const { data: conversation } = useGetSoremonConversationQuery(undefined, {
    skip: !available || !mounted,
  });
  const [sendMessage, { isLoading: sending }] = useSendSoremonMessageMutation();
  const [confirmAction, { isLoading: confirming }] = useConfirmSoremonActionMutation();
  const [cancelAction, { isLoading: cancelling }] = useCancelSoremonActionMutation();
  const [resetConversation, { isLoading: resetting }] = useResetSoremonConversationMutation();

  const attachments = useSoremonAttachments();
  const waiting = sending || confirming || cancelling || resetting || attachments.uploading;
  const { mood, progressMood, progressLabel, showFlash } = useSoremonActivity({
    available,
    open,
    waiting,
    uploading: attachments.uploading,
    hasPendingAction: !!conversation?.has_pending_action,
  });

  const me = useMemo<MessageUser>(() => {
    const user = currentUser?.data;
    return {
      id: user?.id ?? "me",
      display_name: user?.display_name_jp || user?.display_name_en || user?.stage_name || "",
      avatar: user?.avatar_optimized || user?.avatar_original || null,
    };
  }, [currentUser]);

  if (!available || !mounted) return null;

  /** CommentField は false を返すと入力内容を戻す。 */
  const handleSend = async (text: string): Promise<boolean> => {
    const message = text.trim();
    const files = attachments.selectedFiles;
    if ((!message && files.length === 0) || waiting) return false;
    if (message.length > MAX_INPUT_CHARS) {
      toast.error(`${MAX_INPUT_CHARS} 文字以内で入力してください`);
      return false;
    }

    const uploaded = await attachments.upload(files);
    if (uploaded === null || (!message && uploaded.length === 0)) return false;

    setSendingText(message || uploaded.map((a) => a.file_name).join("\n"));
    try {
      await sendMessage({ text: message, attachments: uploaded }).unwrap();
      showFlash("completed");
      return true;
    } catch (err) {
      showFlash("error");
      toast.error(extractErrorMessage(err, ERROR_FALLBACK));
      if (files.length > 0) attachments.restoreFiles(files);
      return false;
    } finally {
      setSendingText(null);
    }
  };

  const runAction = async (run: () => Promise<unknown>) => {
    try {
      await run();
      showFlash("completed");
    } catch (err) {
      showFlash("error");
      toast.error(extractErrorMessage(err, ERROR_FALLBACK));
    }
  };

  const statusText = soremonMoodText(mood, waiting ? progressLabel : "");

  return (
    <Panel
      ref={panelRef}
      $top={panelTop}
      $open={shown}
      className="u-col u-items-stretch u-bg-white u-popover-width"
      aria-label="soremon"
      aria-hidden={!shown}
    >
      <div className="u-row-between u-items-center u-gap8 u-ptb8 u-pl16 u-pr16 u-border-bottom">
        <div className="u-row u-items-center u-gap8">
          <SoremonCharacter mood={mood} size="medium" />
          <div className="u-col">
            <span className="heading-16 u-line-height-100">soremon</span>
            {statusText && (
              <span className="bodytext-11 u-text-light-gray" aria-live="polite">
                {statusText}
              </span>
            )}
          </div>
        </div>
        <IconButton
          icon="restart_alt"
          title="新しい会話をはじめる"
          ariaLabel="新しい会話をはじめる"
          disabled={waiting}
          onClick={() => runAction(() => resetConversation().unwrap())}
        />
      </div>
      <SoremonMessageList
        messages={conversation?.messages ?? []}
        me={me}
        sendingText={sendingText}
        waiting={waiting}
        progressMood={progressMood}
        progressLabel={progressLabel}
        onConfirm={(id) => runAction(() => confirmAction(id).unwrap())}
        onCancel={(id) => runAction(() => cancelAction(id).unwrap())}
        onSuggest={(text) => void handleSend(text)}
      />
      {attachments.uploadingFiles.length > 0 && (
        <div className="u-pl16 u-pr16">
          <UploadProgressBar
            files={attachments.uploadingFiles}
            onCancel={attachments.abortFile}
            onRetry={(file) => void attachments.retryFile(file.id)}
            compact
            fitToParent
            postProcessLabel={attachments.postProcessLabel}
          />
        </div>
      )}
      <div className="u-pt8 u-pb16 u-pl16 u-pr16">
        <CommentField
          onSend={handleSend}
          showPinIcon={false}
          showAttachIcon
          showBottomAppBar={false}
          onFilesSelected={attachments.addFiles}
          selectedFiles={attachments.selectedFiles}
          onRemoveFile={attachments.removeFile}
          multipleFiles
          acceptFileTypes={ACCEPT_FILE_TYPES.join(",")}
          allowEmptySend={attachments.selectedFiles.length > 0}
          placeholder="soremon に話しかける"
        />
      </div>
      {attachments.uploadFailureDialog}
    </Panel>
  );
};

export default SoremonPanel;
